import React, { useState, useEffect } from 'react';
import { Modal, Button } from '../../../../components/ui';
import { projectAPI } from '../../../../services/realApi';
import { Person } from '../hooks/usePeople';
import { TransformedProject } from '../hooks/useProjects';

type PersonMode = 'select' | 'create' | 'edit';

interface PersonFormData {
  employeeId: string;
  name: string;
  email: string;
  role: string;
  hourlyRate: number | string;
}

interface FormErrors {
  employeeId?: string;
  name?: string;
  email?: string;
  role?: string;
  hourlyRate?: string;
}

interface Employee {
  id: string;
  name: string;
  email?: string;
  defaultRole?: string;
  defaultHourlyRate?: number;
}

interface ApiResult {
  success: boolean;
  data?: Employee[];
  error?: string;
}

interface Props {
  project: TransformedProject;
  person?: Person | null;
  onClose: () => void;
  onSuccess: () => void;
}

const AddPersonModal: React.FC<Props> = ({ project, person, onClose, onSuccess }) => {
  const [mode, setMode] = useState<PersonMode>(person ? 'edit' : 'select');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<PersonFormData>({
    employeeId: person?.employeeId ?? '',
    name: person?.name ?? '',
    email: person?.email ?? '',
    role: person?.role ?? '',
    hourlyRate: person?.hourlyRate ?? 150
  });
  const [errors, setErrors] = useState<FormErrors>({});

  useEffect(() => {
    if (mode === 'select' && !person) {
      loadEmployees();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const loadEmployees = async () => {
    setLoading(true);
    try {
      const result: ApiResult = await projectAPI.getPartnerEmployees();
      if (result.success) {
        setEmployees(result.data ?? []);
      } else {
        console.error('Error loading employees:', result.error);
      }
    } catch (err: unknown) {
      console.error('Error loading employees:', err);
    } finally {
      setLoading(false);
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (mode === 'select' && !formData.employeeId) {
      newErrors.employeeId = 'Please select an employee';
    }

    if ((mode === 'create' || mode === 'edit') && !formData.name) {
      newErrors.name = 'Name is required';
    }

    if ((mode === 'create' || mode === 'edit') && !formData.email) {
      newErrors.email = 'Email is required';
    } else if ((mode === 'create' || mode === 'edit') && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if ((mode === 'create' || mode === 'edit') && !formData.role) {
      newErrors.role = 'Role is required';
    }

    if ((mode === 'create' || mode === 'edit') && (!formData.hourlyRate || Number.parseFloat(String(formData.hourlyRate)) <= 0)) {
      newErrors.hourlyRate = 'Hourly rate must be greater than 0';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);
    try {
      let result: { success: boolean; error?: string };

      if (mode === 'edit' && person) {
        result = await projectAPI.updateProjectPerson(project.id, person.id, {
          role: formData.role,
          hourlyRate: Number.parseFloat(String(formData.hourlyRate))
        });
      } else if (mode === 'select') {
        const selectedEmployee = employees.find(e => e.id === formData.employeeId);
        result = await projectAPI.addProjectPerson(project.id, {
          employeeId: formData.employeeId,
          role: selectedEmployee?.defaultRole ?? formData.role ?? 'Team Member',
          hourlyRate: Number.parseFloat(String(selectedEmployee?.defaultHourlyRate ?? formData.hourlyRate))
        });
      } else {
        result = await projectAPI.addProjectPerson(project.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role,
          hourlyRate: Number.parseFloat(String(formData.hourlyRate))
        });
      }

      if (result.success) {
        onSuccess();
      } else {
        alert('Failed to save: ' + result.error);
      }
    } catch (err: unknown) {
      console.error('Error saving person:', err);
      alert('Failed to save person');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof PersonFormData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const getTitle = (): string => {
    if (mode === 'edit') return 'Edit Person';
    if (mode === 'create') return 'Create New Employee';
    return 'Add Person to Project';
  };

  let submitButtonLabel: string;
  if (loading) {
    submitButtonLabel = 'Saving...';
  } else if (mode === 'edit') {
    submitButtonLabel = 'Update';
  } else if (mode === 'create') {
    submitButtonLabel = 'Create & Add to Project';
  } else {
    submitButtonLabel = 'Add to Project';
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={getTitle()}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {mode === 'select' && (
          <>
            <div>
              <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Select Existing Employee
              </span>
              {loading && (
                <div className="flex items-center justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                </div>
              )}
              {!loading && employees.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  No existing employees found.
                </p>
              )}
              {!loading && employees.length > 0 && (
                <select
                  value={formData.employeeId}
                  onChange={(e) => handleChange('employeeId', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.employeeId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">Select an employee</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name} - {emp.email}
                    </option>
                  ))}
                </select>
              )}
              {errors.employeeId && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.employeeId}</p>
              )}
            </div>

            <button
              type="button"
              onClick={() => setMode('create')}
              className="text-blue-600 dark:text-blue-400 text-sm hover:underline"
            >
              + Add New Employee
            </button>
          </>
        )}

        {(mode === 'create' || mode === 'edit') && (
          <>
            {mode === 'create' && (
              <button
                type="button"
                onClick={() => setMode('select')}
                className="text-gray-600 dark:text-gray-400 text-sm hover:underline flex items-center gap-1"
              >
                <span>←</span> Back to Existing
              </button>
            )}

            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Name *
              </label>
              <input id="name"
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={loading || mode === 'edit'}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.name}</p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Email *
              </label>
              <input id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={loading || mode === 'edit'}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.email}</p>
              )}
            </div>

            <div>
              <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Role *
              </label>
              <input id="role"
                type="text"
                value={formData.role}
                onChange={(e) => handleChange('role', e.target.value)}
                placeholder="e.g., Senior Developer, Project Manager"
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.role ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={loading}
              />
              {errors.role && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.role}</p>
              )}
            </div>

            <div>
              <label htmlFor="hourly-rate-usd" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Hourly Rate (USD)
              </label>
              <input id="hourly-rate-usd"
                type="number"
                step="0.01"
                min="0"
                value={formData.hourlyRate}
                onChange={(e) => handleChange('hourlyRate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.hourlyRate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                disabled={loading}
              />
              {errors.hourlyRate && (
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.hourlyRate}</p>
              )}
            </div>
          </>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={loading}
          >
            {submitButtonLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default AddPersonModal;
